import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { errorMessage } from "../../../api/client";
import { messaging, type OptOut, optOutsQuery } from "../../../api/messaging";
import { useCanEdit } from "../../../auth/useMe";
import { OptOutTable } from "../../../components/messaging/OptOutTable";
import { toast } from "../../../components/toast";
import { Button, Card, Dialog, ErrorState, Field, Input, Loading } from "../../../components/ui";
import { fmtPhone } from "../../../lib/format";
import { useDebounced } from "../../../lib/useDebounced";

export const Route = createFileRoute("/admin/messages/opt-outs")({
  staticData: { crumb: { label: "Opt-outs", parents: [] } },
  component: OptOutsPage,
});

function OptOutsPage() {
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const [text, setText] = useState("");
  const q = useDebounced(text.trim(), 250);
  const list = useQuery(optOutsQuery(q));
  const [adding, setAdding] = useState(false);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<OptOut | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "texting"] });
  const onRemove = useCallback((o: OptOut) => setRemoving(o), []);

  const add = async () => {
    setBusy(true);
    setAddError(null);
    try {
      await messaging.addOptOut(number.trim());
      setAdding(false);
      setNumber("");
      refresh();
      toast("Number added to the opt-out list.");
    } catch (e) {
      setAddError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          type="search"
          aria-label="Search opt-outs"
          placeholder="Name or number"
          className="w-64"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {list.data ? (
          <span className="tnum text-caption text-ink-3">
            {list.data.total === 1 ? "1 number" : `${list.data.total} numbers`}
          </span>
        ) : null}
        {canEdit ? (
          <Button className="ml-auto" onClick={() => setAdding(true)}>
            <Plus />
            Add number
          </Button>
        ) : null}
      </div>
      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : (
          <OptOutTable items={list.data.items} canEdit={canEdit} filtered={Boolean(q)} onRemove={onRemove} />
        )}
      </Card>
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a number"
        description="Texts to this number are blocked until you remove it."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!number.trim()} busy={busy} onClick={add}>
              Add
            </Button>
          </>
        }
      >
        <Field label="Phone number" error={addError}>
          <Input
            type="tel"
            placeholder="(816) 555-0100"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
        </Field>
      </Dialog>
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing ? fmtPhone(removing.e164) : ""}?`}
        description="Reps can text this number again."
        size="sm"
        tone="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              busy={busy}
              onClick={async () => {
                if (!removing) return;
                setBusy(true);
                try {
                  await messaging.removeOptOut(removing.e164);
                  refresh();
                  toast("Number removed from the opt-out list.");
                } catch (e) {
                  toast(errorMessage(e), "danger");
                } finally {
                  setBusy(false);
                  setRemoving(null);
                }
              }}
            >
              Remove
            </Button>
          </>
        }
      />
    </>
  );
}
